import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { publicApiClient } from '@/api/public'
import { moneyFor } from '@/lib/money'
import { datesFor } from '@/lib/dates'
import { usePageMeta } from '@/hooks/usePageMeta'
import { BrandHeader, CourseCard, NotFound, PageSpinner } from './PublicCourseParts'
import { brandVars } from './brand'

/** A customer's course catalogue: their courses that are switched on for their website, grouped by category. */
export default function PublicCoursesPage() {
  const { slug } = useParams<{ slug: string }>()
  const [categorySlug, setCategorySlug] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-courses', slug],
    queryFn: () => publicApiClient.getPublicCourseCatalogue(slug!).then(r => r.data),
    enabled: !!slug,
  })
  const { fmt: fmtMoney } = moneyFor(data?.currency)
  const { formatDate } = datesFor(data?.region, data?.timeZoneId)

  usePageMeta({
    title: data ? `Courses | ${data.tenantName}` : 'Courses',
    description: data ? `Training courses from ${data.tenantName}: dates, prices and booking.` : null,
  })

  if (isLoading) return <PageSpinner />
  if (isError || !data) return <NotFound title="Courses not found" hint="Check the link and try again." />

  const selected = data.categories.find(c => c.slug === categorySlug) ?? null
  const courses = selected ? data.courses.filter(c => c.categorySlug === selected.slug) : data.courses

  return (
    <div className="min-h-screen bg-slate-50" style={brandVars(data)}>
      <BrandHeader
        brand={data}
        subtitle="Our courses"
        right={<Link to={`/sessions/${slug}`} className="hover:opacity-80">Upcoming sessions →</Link>}
      />

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {data.categories.length > 1 && (
          <nav aria-label="Course categories" className="flex flex-wrap gap-2">
            {[{ slug: null as string | null, name: 'All courses' }, ...data.categories].map(c => {
              const active = c.slug === (selected?.slug ?? null)
              return (
                <button
                  key={c.slug ?? 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setCategorySlug(c.slug)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                    active ? 'border-transparent bg-[var(--brand-accent)] text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {c.name}
                </button>
              )
            })}
          </nav>
        )}

        {selected?.description && <p className="max-w-2xl text-slate-600">{selected.description}</p>}

        {courses.length === 0 ? (
          <p className="py-16 text-center text-slate-500">No courses to show yet.</p>
        ) : (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map(course => (
              <li key={course.slug}>
                <CourseCard
                  tenantSlug={slug!}
                  course={course}
                  category={data.categories.find(c => c.slug === course.categorySlug)}
                  fmtMoney={fmtMoney}
                  formatDate={formatDate}
                />
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  )
}
